import React, { memo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import 'bloko/blocks/button/button.less';

import { componentInvariants } from 'bloko/common/helpers/customPropTypes';
import ButtonGroup from 'bloko/blocks/button/ButtonGroup';

const renderIcon = (icon, iconPosition) =>
    icon && (
        <span
            key="icon"
            className={classnames('bloko-button__icon', {
                [`bloko-button__icon_${iconPosition}`]: iconPosition,
            })}
        >
            {icon}
        </span>
    );

/**
 * Кнопка общего назначения, используется на различных формах, для реализации элементов управления.
 */
/* eslint-disable-next-line prefer-arrow-callback */
const Button = memo(function Button({
    scale,
    kind,
    type,
    children,
    pressed,
    innerRef,
    Element,
    icon,
    iconPosition,
    stretched,
    loading,
    invalid,
    marked,
    ...buttonProps
}) {
    const renderContent = () => {
        if (loading) {
            return (
                <React.Fragment>
                    <span className="bloko-button__content">{children}</span>
                    <span className="bloko-button__loading">{loading}</span>
                </React.Fragment>
            );
        }

        if (icon && iconPosition === 'right') {
            return [children, renderIcon(icon, iconPosition)];
        }

        return [renderIcon(icon, iconPosition), children];
    };

    const className = classnames('bloko-button', {
        [`bloko-button_${kind}`]: kind,
        [`bloko-button_${scale}`]: scale,
        'bloko-button_pressed': pressed,
        'bloko-button_stretched': stretched,
        'bloko-button_loading': !!loading,
        'bloko-button_invalid': invalid,
        'bloko-button_marked': marked,
    });

    const typeAttribute = {};

    if (Element !== 'a' && type) {
        typeAttribute.type = type;
    }

    return (
        <Element className={className} ref={innerRef} {...typeAttribute} {...buttonProps}>
            {renderContent()}
        </Element>
    );
});

Button.kinds = {
    primary: 'primary',
    primaryMinor: 'primary-minor',
    secondary: 'secondary',
    secondaryMinor: 'secondary-minor',
    tertiary: 'tertiary',
    tertiaryMinor: 'tertiary-minor',
    primaryDimmed: 'primary-dimmed',
    primaryDimmedMinor: 'primary-dimmed-minor',
    minor: 'minor',
    iconOnly: 'icon-only',
    promo: 'promo',
    radio: 'radio',
};

Button.iconPositions = {
    left: 'left',
    right: 'right',
};

Button.propTypes = {
    /** Размеры кнопки */
    scale: PropTypes.oneOf(['small', 'large']),
    /** Тип компонента по назначению возможные варианты [Button.kinds](#button-kind) */
    kind: PropTypes.oneOf(Object.values(Button.kinds)),
    /** Тип кнопки по назначению */
    type: PropTypes.oneOf(['submit', 'button']),
    /** Контент кнопки */
    children: PropTypes.node,
    /** Нажата ли кнопка */
    pressed: PropTypes.bool,
    /** Заблокирована ли кнопка */
    disabled: PropTypes.bool,
    /** Функция для получения DOM элемента кнопки */
    innerRef: PropTypes.func,
    /** Кастомный компонент 'a', 'button', 'span' или функция */
    Element: componentInvariants(['a', 'button', 'span']),
    /** onClick callback */
    onClick: PropTypes.func,
    /** На всю длину */
    stretched: PropTypes.bool,
    /** Активированность */
    marked: PropTypes.bool,
    /** Валидность */
    invalid: PropTypes.bool,
    /** Отображать индикацию загрузки */
    loading: PropTypes.node,
    /** Иконка */
    icon: PropTypes.node,
    /** Положение иконки */
    iconPosition: PropTypes.oneOf(Object.values(Button.iconPositions)),
};

Button.defaultProps = {
    pressed: false,
    disabled: false,
    invalid: false,
    marked: false,
    stretched: false,
    type: 'button',
    Element: 'button',
};

export default Button;
export { ButtonGroup };
